import type {
  AdminUsageDashboardResponse,
  UsageByKeyResponse,
  UsageDailyResponse,
  UsageHourlyResponse,
  UsageRequestLogsResponse,
  UsageSummaryResponse,
} from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  getUsageByKey,
  browserTimeZone,
  getUsageDaily,
  getUsageHourly,
  getUsageRequests,
  getUsageSummary,
  getAdminUsageDashboard,
  type AdminUsageDashboardClientQuery,
  type UsageRequestsQuery,
  type UsageRange,
} from './usage.ts';

/**
 * React Query wrappers for tenant usage queries.
 *
 * Query key conventions:
 *   - `['usage', 'summary', from|'', to|'']`      — summary (range-keyed)
 *   - `['usage', 'hourly', days]`                 — hourly bucket series
 *   - `['usage', 'daily', days]`                  — daily bucket series
 *   - `['usage', 'by-key', from|'', to|'']`       — per-key breakdown
 *
 * Ranges are optional; an `undefined` range collapses to empty strings
 * in the key so "no filter" stays deterministic across renders. No
 * mutations; usage data is read-only.
 */

function rangeKey(range: UsageRange | undefined): readonly [string, string] {
  return [range?.from ?? '', range?.to ?? ''] as const;
}

export function useUsageSummary(range?: UsageRange): UseQueryResult<UsageSummaryResponse> {
  const { fetchImpl } = useSession();
  const [from, to] = rangeKey(range);
  return useQuery({
    queryKey: ['usage', 'summary', from, to] as const,
    queryFn: ({ signal }) => getUsageSummary(fetchImpl, range, signal),
  });
}

export function useUsageHourly(days: number): UseQueryResult<UsageHourlyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['usage', 'hourly', days] as const,
    queryFn: ({ signal }) => getUsageHourly(fetchImpl, days, signal),
  });
}

export function useUsageDaily(days: number): UseQueryResult<UsageDailyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    // #1678: the bucketing zone decides which calendar `days[].date` belongs
    // to, so it belongs in the key like any other query dimension. This hook
    // takes no `tz` — the client sends the browser's own — so the key reads it
    // from the same source rather than inventing a second one.
    queryKey: ['usage', 'daily', days, browserTimeZone() ?? ''] as const,
    queryFn: ({ signal }) => getUsageDaily(fetchImpl, days, signal),
  });
}

export function useUsageByKey(range?: UsageRange): UseQueryResult<UsageByKeyResponse> {
  const { fetchImpl } = useSession();
  const [from, to] = rangeKey(range);
  return useQuery({
    queryKey: ['usage', 'by-key', from, to] as const,
    queryFn: ({ signal }) => getUsageByKey(fetchImpl, range, signal),
  });
}

export function useUsageRequests(
  query?: UsageRequestsQuery,
): UseQueryResult<UsageRequestLogsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [
      'usage',
      'requests',
      query?.from ?? '',
      query?.to ?? '',
      query?.requestId ?? '',
      query?.apiKeyId ?? '',
      query?.status ?? '',
      query?.limit,
      query?.offset,
    ] as const,
    queryFn: ({ signal }) => getUsageRequests(fetchImpl, query, signal),
  });
}

export function useAdminUsageDashboard(
  query?: AdminUsageDashboardClientQuery,
  enabled = true,
): UseQueryResult<AdminUsageDashboardResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [
      'usage',
      'admin-dashboard',
      query?.from ?? '',
      query?.to ?? '',
      query?.granularity ?? '',
      query?.groupBy ?? '',
      // The model dimension is keyed by vendor + model together (#1672);
      // a key holding only the name lets two vendors of one model serve
      // each other's cached aggregates (#1685).
      query?.model ?? '',
      query?.vendor ?? '',
      query?.apiKeyId ?? '',
      query?.memberAccountId ?? '',
      query?.department ?? '',
      query?.q ?? '',
      query?.limit ?? '',
      query?.offset ?? '',
      // #1678: the bucketing zone changes the bucket labels, so it belongs in
      // the cache key like any other query dimension.
      query?.tz ?? '',
    ] as const,
    queryFn: ({ signal }) => getAdminUsageDashboard(fetchImpl, query, signal),
    enabled,
  });
}
